import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import {
  Shield, Search, Download, RefreshCw, Loader2,
  Eye, FileText, User, Clock, Filter, X,
  BarChart3, Calendar, AlertTriangle, Trash2
} from 'lucide-react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuditoriaRGPD() {
  const { toast } = useToast();
  const [tabActiva, setTabActiva] = useState('logs');
  const [logs, setLogs] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [actividad, setActividad] = useState(null);
  const [periodoActividad, setPeriodoActividad] = useState('0');
  const [cargandoActividad, setCargandoActividad] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    fecha_desde: '',
    fecha_hasta: '',
    usuario_id: '',
    usuario: '',
    accion: '',
    recurso: ''
  });

  const COLORES = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  useEffect(() => {
    cargarLogs();
    cargarEstadisticas();
    cargarUsuarios();
  }, []);

  useEffect(() => {
    if (tabActiva === 'estadisticas') cargarActividad();
    // eslint-disable-next-line
  }, [tabActiva, periodoActividad]);

  const cargarUsuarios = async () => {
    try {
      const response = await axios.get(`${API}/auditoria/usuarios`);
      setUsuarios(response.data.usuarios || []);
    } catch (err) {
      console.error('Error cargando usuarios:', err);
    }
  };

  const cargarActividad = async () => {
    setCargandoActividad(true);
    try {
      const response = await axios.get(`${API}/auditoria/estadisticas-usuarios?dias=${periodoActividad}`);
      setActividad(response.data);
    } catch (err) {
      console.error('Error cargando actividad por usuario:', err);
      toast({ title: 'Error', description: 'No se pudo calcular la actividad por usuario', variant: 'destructive' });
    } finally {
      setCargandoActividad(false);
    }
  };

  const cargarLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtros.fecha_desde) params.append('fecha_desde', filtros.fecha_desde);
      if (filtros.fecha_hasta) params.append('fecha_hasta', filtros.fecha_hasta);
      if (filtros.usuario_id && filtros.usuario_id !== 'all') params.append('usuario_id', filtros.usuario_id);
      if (filtros.usuario && filtros.usuario.trim()) params.append('usuario', filtros.usuario.trim());
      if (filtros.accion && filtros.accion !== 'all') params.append('accion', filtros.accion);
      if (filtros.recurso && filtros.recurso !== 'all') params.append('recurso', filtros.recurso);
      params.append('limite', '300');

      const response = await axios.get(`${API}/auditoria/logs?${params.toString()}`);
      setLogs(response.data.logs || []);
    } catch (err) {
      console.error('Error cargando logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const cargarEstadisticas = async () => {
    try {
      const response = await axios.get(`${API}/auditoria/estadisticas?dias=30`);
      setEstadisticas(response.data);
    } catch (err) {
      console.error('Error cargando estadísticas:', err);
    }
  };

  const exportarLogs = async (formato) => {
    try {
      const response = await axios.get(`${API}/auditoria/exportar?formato=${formato}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `auditoria_rgpd.${formato}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({ title: 'Exportación completada', description: `Archivo ${formato.toUpperCase()} descargado` });
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudo exportar', variant: 'destructive' });
    }
  };

  const getAccionBadge = (accion) => {
    const estilos = {
      login: 'bg-blue-100 text-blue-700',
      logout: 'bg-gray-100 text-gray-700',
      ver: 'bg-green-100 text-green-700',
      crear: 'bg-emerald-100 text-emerald-700',
      modificar: 'bg-yellow-100 text-yellow-700',
      eliminar: 'bg-red-100 text-red-700',
      exportar: 'bg-purple-100 text-purple-700',
      acceso_denegado: 'bg-red-200 text-red-800'
    };
    return estilos[accion] || 'bg-gray-100 text-gray-700';
  };

  const limpiarFiltros = () => {
    setFiltros({
      fecha_desde: '',
      fecha_hasta: '',
      usuario_id: '',
      usuario: '',
      accion: '',
      recurso: ''
    });
  };

  const etiquetaUsuario = (u) => `${u.nombre || u.email}${u.activo ? '' : ' (inactivo)'}`;

  const exportarActividadCSV = () => {
    if (!actividad?.usuarios?.length) return;
    const cols = ['usuario_id', 'nombre', 'email', 'rol', 'activo', 'total', 'logins', 'logouts',
      'vistas', 'creaciones', 'modificaciones', 'eliminaciones', 'exportaciones', 'busquedas',
      'denegados', 'dias_activos', 'ips_distintas', 'primer_acceso', 'ultimo_acceso'];
    const lineas = [cols.join(';')];
    actividad.usuarios.forEach((u) => {
      lineas.push(cols.map((c) => (u[c] === null || u[c] === undefined ? '' : String(u[c]).replace(/;/g, ','))).join(';'));
    });
    const url = window.URL.createObjectURL(new Blob(['\uFEFF' + lineas.join('\n')], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'actividad_por_usuario.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            Auditoría RGPD
          </h1>
          <p className="text-gray-600">Registro de accesos y operaciones para cumplimiento normativo</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exportarLogs('csv')}>
            <Download className="h-4 w-4 mr-2" /> CSV
          </Button>
          <Button variant="outline" onClick={() => exportarLogs('json')}>
            <Download className="h-4 w-4 mr-2" /> JSON
          </Button>
        </div>
      </div>

      <Tabs value={tabActiva} onValueChange={setTabActiva}>
        <TabsList className="mb-4">
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <FileText className="h-4 w-4" /> Logs de Acceso
          </TabsTrigger>
          <TabsTrigger value="estadisticas" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" /> Estadísticas
          </TabsTrigger>
        </TabsList>

        {/* Logs */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Registro de Accesos</CardTitle>
              <CardDescription>Últimos {logs.length} registros</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filtros */}
              <div className="grid md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs">Usuario</Label>
                  <Select
                    value={filtros.usuario_id || 'all'}
                    onValueChange={(v) => setFiltros({ ...filtros, usuario_id: v === 'all' ? '' : v })}
                  >
                    <SelectTrigger data-testid="rgpd-filtro-usuario-trigger">
                      <SelectValue placeholder="Todos los usuarios" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los usuarios</SelectItem>
                      {usuarios.map((u) => (
                        <SelectItem key={u.id} value={String(u.id)} data-testid={`rgpd-filtro-usuario-${u.id}`}>
                          {etiquetaUsuario(u)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Buscar usuario (nombre o email)</Label>
                  <Input
                    placeholder="p. ej. eduard"
                    value={filtros.usuario}
                    data-testid="rgpd-buscar-usuario"
                    onChange={(e) => setFiltros({ ...filtros, usuario: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') cargarLogs(); }}
                  />
                </div>
                <div>
                  <Label className="text-xs">Desde</Label>
                  <Input
                    type="date"
                    value={filtros.fecha_desde}
                    onChange={(e) => setFiltros({ ...filtros, fecha_desde: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Hasta</Label>
                  <Input
                    type="date"
                    value={filtros.fecha_hasta}
                    onChange={(e) => setFiltros({ ...filtros, fecha_hasta: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Acción</Label>
                  <Select value={filtros.accion} onValueChange={(v) => setFiltros({ ...filtros, accion: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="login">Login</SelectItem>
                      <SelectItem value="ver">Ver</SelectItem>
                      <SelectItem value="crear">Crear</SelectItem>
                      <SelectItem value="modificar">Modificar</SelectItem>
                      <SelectItem value="eliminar">Eliminar</SelectItem>
                      <SelectItem value="exportar">Exportar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Recurso</Label>
                  <Select value={filtros.recurso} onValueChange={(v) => setFiltros({ ...filtros, recurso: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="expediente">Expediente</SelectItem>
                      <SelectItem value="contacto">Contacto</SelectItem>
                      <SelectItem value="factura">Factura</SelectItem>
                      <SelectItem value="documento">Documento</SelectItem>
                      <SelectItem value="usuario">Usuario</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={cargarLogs} className="flex-1" data-testid="rgpd-filtrar-btn">
                    <Search className="h-4 w-4 mr-1" /> Filtrar
                  </Button>
                  <Button variant="outline" onClick={limpiarFiltros} data-testid="rgpd-limpiar-btn">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Tabla de logs */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3">Fecha</th>
                        <th className="text-left p-3">Usuario</th>
                        <th className="text-left p-3">Acción</th>
                        <th className="text-left p-3">Recurso</th>
                        <th className="text-left p-3">Detalles</th>
                        <th className="text-left p-3">IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-xs text-gray-600">
                            {new Date(log.fecha).toLocaleString('es-ES')}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <div className="leading-tight">
                                <div className="text-sm">{log.usuario_nombre || log.usuario_email}</div>
                                {log.usuario_nombre && (
                                  <div className="text-xs text-gray-500">{log.usuario_email}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge className={getAccionBadge(log.accion)}>
                              {log.accion}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="leading-tight">
                              <div className="text-sm">
                                {log.recurso}
                                {log.referencia && (
                                  <span className="ml-1 font-medium text-gray-900">{log.referencia}</span>
                                )}
                                {!log.referencia && log.recurso_id && (
                                  <span className="text-xs text-gray-500 ml-1">
                                    #{log.recurso_id}{log.referencia_estado === 'no_encontrado' ? ' (ya no existe)' : ''}
                                  </span>
                                )}
                                {!log.recurso_id && (
                                  <span className="text-xs text-gray-400 ml-1">(listado)</span>
                                )}
                              </div>
                              {log.referencia_detalle && (
                                <div className="text-xs text-gray-500 max-w-xs truncate">{log.referencia_detalle}</div>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-xs text-gray-600 max-w-xs truncate">
                            {log.detalles || '-'}
                          </td>
                          <td className="p-3 text-xs text-gray-500">{log.ip_origen || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {logs.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No hay registros que mostrar</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Estadísticas */}
        <TabsContent value="estadisticas">
          {/* Actividad exacta de TODOS los usuarios */}
          <Card className="mb-6">
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle className="text-base">Actividad de accesos por usuario</CardTitle>
                <CardDescription>
                  {actividad
                    ? `${actividad.total_usuarios} usuarios · ${actividad.total_registros.toLocaleString('es-ES')} registros ${actividad.periodo_dias ? `de los últimos ${actividad.periodo_dias} días` : 'de todo el histórico'}`
                    : 'Cifras exactas de cada usuario, incluidos los que no tienen actividad'}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Select value={periodoActividad} onValueChange={setPeriodoActividad}>
                  <SelectTrigger className="w-[190px]" data-testid="rgpd-periodo-trigger">
                    <SelectValue placeholder="Periodo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Todo el histórico</SelectItem>
                    <SelectItem value="7">Últimos 7 días</SelectItem>
                    <SelectItem value="30">Últimos 30 días</SelectItem>
                    <SelectItem value="90">Últimos 90 días</SelectItem>
                    <SelectItem value="365">Último año</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={cargarActividad} data-testid="rgpd-recargar-actividad">
                  <RefreshCw className={`h-4 w-4 ${cargandoActividad ? 'animate-spin' : ''}`} />
                </Button>
                <Button variant="outline" onClick={exportarActividadCSV} data-testid="rgpd-exportar-actividad">
                  <Download className="h-4 w-4 mr-2" /> CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {cargandoActividad && !actividad ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="rgpd-tabla-actividad">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-2">Usuario</th>
                        <th className="text-right p-2">Total</th>
                        <th className="text-right p-2">Logins</th>
                        <th className="text-right p-2">Consultas</th>
                        <th className="text-right p-2">Altas</th>
                        <th className="text-right p-2">Modif.</th>
                        <th className="text-right p-2">Bajas</th>
                        <th className="text-right p-2">Export.</th>
                        <th className="text-right p-2">Denegados</th>
                        <th className="text-right p-2">Días activos</th>
                        <th className="text-right p-2">IPs</th>
                        <th className="text-left p-2">Primer acceso</th>
                        <th className="text-left p-2">Último acceso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(actividad?.usuarios || []).map((u) => (
                        <tr key={`${u.usuario_id}-${u.email}`} className="border-b hover:bg-gray-50"
                            data-testid={`rgpd-actividad-fila-${u.usuario_id}`}>
                          <td className="p-2">
                            <div className="leading-tight">
                              <div className="font-medium">
                                {u.nombre || u.email || `Usuario #${u.usuario_id}`}
                                {!u.existe && <Badge className="ml-2 bg-gray-200 text-gray-700">eliminado</Badge>}
                                {u.existe && !u.activo && <Badge className="ml-2 bg-yellow-100 text-yellow-700">inactivo</Badge>}
                              </div>
                              <div className="text-xs text-gray-500">{u.email} {u.rol ? `· ${u.rol}` : ''}</div>
                            </div>
                          </td>
                          <td className="p-2 text-right font-semibold">{u.total.toLocaleString('es-ES')}</td>
                          <td className="p-2 text-right">{u.logins.toLocaleString('es-ES')}</td>
                          <td className="p-2 text-right">{u.vistas.toLocaleString('es-ES')}</td>
                          <td className="p-2 text-right">{u.creaciones.toLocaleString('es-ES')}</td>
                          <td className="p-2 text-right">{u.modificaciones.toLocaleString('es-ES')}</td>
                          <td className="p-2 text-right">{u.eliminaciones.toLocaleString('es-ES')}</td>
                          <td className="p-2 text-right">{u.exportaciones.toLocaleString('es-ES')}</td>
                          <td className={`p-2 text-right ${u.denegados > 0 ? 'text-red-600 font-semibold' : ''}`}>
                            {u.denegados.toLocaleString('es-ES')}
                          </td>
                          <td className="p-2 text-right">{u.dias_activos.toLocaleString('es-ES')}</td>
                          <td className="p-2 text-right">{u.ips_distintas.toLocaleString('es-ES')}</td>
                          <td className="p-2 text-xs text-gray-600">
                            {u.primer_acceso ? new Date(u.primer_acceso).toLocaleString('es-ES') : '-'}
                          </td>
                          <td className="p-2 text-xs text-gray-600">
                            {u.ultimo_acceso ? new Date(u.ultimo_acceso).toLocaleString('es-ES') : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {actividad && actividad.usuarios.length === 0 && (
                    <div className="text-center py-10 text-gray-500">No hay actividad registrada</div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {estadisticas ? (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Por Acción */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Accesos por Tipo de Acción</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={estadisticas.por_accion}
                          dataKey="total"
                          nameKey="accion"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ accion, total }) => `${accion}: ${total}`}
                        >
                          {estadisticas.por_accion?.map((entry, index) => (
                            <Cell key={index} fill={COLORES[index % COLORES.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Por Usuario */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top Usuarios con más Accesos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={estadisticas.por_usuario} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="email" type="category" width={150} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="total" fill="#3B82F6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Por Recurso */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Accesos por Recurso</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={estadisticas.por_recurso}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="recurso" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="total" fill="#10B981" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Evolución diaria */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Evolución de Accesos (Últimos 30 días)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={estadisticas.por_dia}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="fecha" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="total" stroke="#8B5CF6" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
